import React from 'react';
import PropTypes from 'prop-types';
import { FocusScope } from '@react-aria/focus';
import { useEditor, useEditingCond, useNode } from '@splashthat/splash-cms-engine';
import Close from 'common/icons/CloseForm';

import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_HOVER_COLOR,
} from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';

import { Modal as ManagedModal } from 'common/components/TouchpointEditor/contexts/ModalManager';

import { ModalContainer, ModalWrapper, CloseButtonContainer, ModalGlobalStyles } from './Modal.styles';

const editorSelector = (state) => ({ editing: state.editing });
const preventModalClickFromClosing = (e) => e.stopPropagation();
const UNRESOLVED_MERGE_TAG = /^\[[^\]]+\]$/;
const FALLBACK_DIALOG_LABEL = 'Confirmation';

export default function Modal({ descendants: { modalContent, closeButton }, hovered, selected, id, children }) {
    const { theme } = useTouchpointData();
    const editor = useEditor(editorSelector);
    const node = useNode();
    const customCssClassName = getCustomCssClassName(node);

    const wrapperRef = React.useRef(null);
    const titleId = React.useId();

    const applyName = React.useCallback(
        (wrapper) => {
            if (!wrapper) return;

            const heading = wrapper.querySelector('h1, h2, h3, h4, h5, h6');
            const headingText = heading ? heading.textContent.trim() : '';

            // An empty heading, or one still holding an unresolved merge tag such as
            // "[confirmation_headline]", would leave the dialog unnamed or be announced
            // verbatim. Use a static label instead of pointing at it.
            if (!headingText || UNRESOLVED_MERGE_TAG.test(headingText)) {
                wrapper.removeAttribute('aria-labelledby');
                wrapper.setAttribute('aria-label', FALLBACK_DIALOG_LABEL);
                return;
            }

            if (!heading.id) {
                heading.id = titleId;
            }
            wrapper.removeAttribute('aria-label');
            wrapper.setAttribute('aria-labelledby', heading.id);
        },
        [titleId],
    );

    // Callback ref: fires when the dialog mounts. ManagedModal owns the open state and
    // calls this render prop without re-rendering Modal, so a plain useEffect without
    // this ref would miss the initial mount and the dialog would stay unnamed.
    const nameDialogByHeading = React.useCallback(
        (wrapper) => {
            wrapperRef.current = wrapper;
            applyName(wrapper);
        },
        [applyName],
    );

    // Re-sync the accessible name after every render so it stays current when heading
    // text changes while the dialog is already mounted (e.g. editor mode: an author
    // edits the headline in place, or the CMS resolves a merge tag). The DOM writes
    // are idempotent, so running on every render is safe.
    React.useEffect(() => {
        applyName(wrapperRef.current);
    });
    const onCloseCallbackIfOnEditorMode = useEditingCond(() => editor.actions.unselectAllNodes());
    const isEndUser = !editor.editing;

    return (
        <ManagedModal modalId={id}>
            {({ isOpen, onResolve }) => {
                const shouldShowModal = isOpen || selected;

                const onClose = onCloseCallbackIfOnEditorMode || onResolve;

                if (!shouldShowModal) {
                    return null;
                }

                return (
                    <ModalContainer
                        className={`${id} cms-engine-modal`}
                        data-qa={`ModalContainer__${id}`}
                        onClick={onClose}
                    >
                        <ModalGlobalStyles isEditing={editor.editing} />
                        <FocusScope contain={isEndUser} restoreFocus autoFocus>
                            <ModalWrapper
                                ref={nameDialogByHeading}
                                data-qa={`${id}__modal-wrapper`}
                                $backgroundColor={theme.colors[modalContent.styles.backgroundColor]}
                                $padding={modalContent.styles.padding}
                                $gap={modalContent.styles.gap}
                                $mainDirection={modalContent.styles.alignment.mainDirection}
                                $perpendicularDirection={modalContent.styles.alignment.perpendicularDirection}
                                $direction={modalContent.styles.direction}
                                role="dialog"
                                aria-modal="true"
                                selected={selected}
                                hovered={hovered}
                                onMouseLeave={node.actions.onMouseLeave()}
                                onMouseMove={node.actions.onMouseMove()}
                                onClick={node.actions.onClick(preventModalClickFromClosing)}
                                className={customCssClassName}
                            >
                                <CloseButtonContainer
                                    $color={theme.colors[closeButton.styles.color]}
                                    $colorHover={theme.colors[closeButton.styles.colorHover]}
                                    onClick={onClose}
                                    data-qa={id}
                                    aria-label="Close"
                                >
                                    <Close />
                                </CloseButtonContainer>
                                {children}
                            </ModalWrapper>
                        </FocusScope>
                    </ModalContainer>
                );
            }}
        </ManagedModal>
    );
}

Modal.splash = {
    related: {
        settings: React.lazy(() => import('./Modal.tools')),
    },
    defaultProps: {
        descendants: {
            modalContent: {
                styles: {
                    backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    padding: {
                        top: 64,
                        bottom: null,
                        left: 48,
                        right: null,
                    },
                    gap: 32,
                    alignment: {
                        mainDirection: 'start',
                        perpendicularDirection: 'start',
                    },
                    direction: 'column',
                },
            },
            closeButton: {
                styles: {
                    color: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    colorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                },
            },
        },
    },
};

Modal.propTypes = {
    descendants: PropTypes.object.isRequired,
    hovered: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};
